import React, {useEffect} from 'react';
import {Form, Input, Select} from 'antd';
import EditableDrawer from '../../../../components/EditableDrawer';
import {WarehouseOption} from '../../../../hooks/useWarehouseOptions';
import { useTranslation } from 'react-i18next';
import {CarrierType} from "../../../../graphql/types/CarrierType";
import { ALL_CENTER_WAREHOUSE_ID } from '../../../../enums/TmsEnum';



interface CarrierFormDrawerProps {
    warehousesOptions: WarehouseOption[];
    visible: boolean;
    onClose: () => void;
    onSubmit: (values: CarrierType) => void;
    carrier: CarrierType | null;
    mode: 'create' | 'edit';
}

const CarrierFormDrawer: React.FC<CarrierFormDrawerProps> = ({
    warehousesOptions,
                                                         visible,
                                                         onClose,
                                                         onSubmit,
                                                                 carrier,
                                                         mode
                                                     }) => {
    const [form] = Form.useForm();
    const {t} = useTranslation();
    const title = mode === 'create' ? t('central.carrier.pageDetails.new') : t('central.carrier.pageDetails.carrierDetails');

    // 转换warehouse数据为Select选项，并在首位追加 全国(0)
    const warehouseOptions = (() => {
        const hasNationwide = warehousesOptions.some(w => String(w.id) === ALL_CENTER_WAREHOUSE_ID);
        const extended = hasNationwide ? warehousesOptions : [{ id: ALL_CENTER_WAREHOUSE_ID, name: t('common.allCenterWarehouse') }, ...warehousesOptions];
        return extended.map(warehouse => ({ value: warehouse.id, label: warehouse.name }));
    })();

    // Reset form when carrier data or mode changes
    useEffect(() => {
        if (visible) {
            form.resetFields();

            if (carrier) {
                // 编辑模式：使用carrier数据和warehouse关联数据
                // 确保 centerWarehouseIds 是数组格式
                const centerWarehouseIds = Array.isArray(carrier.centerWarehouseIds) 
                    ? carrier.centerWarehouseIds 
                    : carrier.centerWarehouseIds != null 
                        ? [carrier.centerWarehouseIds] 
                        : [];
                
                form.setFieldsValue({
                    ...carrier,
                    centerWarehouseIds
                });
            } else {
                // 新建模式：设置默认值
                const today = new Date();
                const formattedDate = `${today.getFullYear()}/${String(today.getMonth() + 1).padStart(2, '0')}/${String(today.getDate()).padStart(2, '0')}`;

                form.setFieldsValue({
                    carrierCode: '',
                    createdAt: formattedDate,
                    status: 1,
                    centerWarehouseIds: []
                });
            }
        }
    }, [visible, carrier, mode, form]);

    const handleSubmit = () => {
        form.validateFields()
            .then(values => {
                // 新建模式时不传递空的 id 字段
                if (mode === 'create') {
                    const {id, ...submitValues} = values;
                    onSubmit(submitValues as CarrierType);
                    return;
                }
                onSubmit(values);
            })
            .catch(info => {
                console.log('Validate Failed:', info);
            });
    };

    return (
        <EditableDrawer
            title={title}
            onClose={onClose}
            visible={visible}
            width={600}
            onSubmit={handleSubmit}
        >
            <Form
                form={form}
                layout="horizontal"
                labelAlign="left"
                labelCol={{span: 6}}
                wrapperCol={{span: 18}}
            >
                {mode === 'edit' && (
                    <Form.Item
                        name="id"
                        hidden={ true}
                        label={t('central.carrier.pageDetails.carrierId')}
                    >
                        <Input disabled={true}/>
                    </Form.Item>
                )}

                {mode === 'edit' && (
                    <Form.Item
                        name="carrierCode"

                        label={t('central.carrier.pageDetails.carrierId')}
                    >
                        <Input disabled={true}/>
                    </Form.Item>
                )}

                <Form.Item
                    name="carrierName"
                    label={t('central.carrier.pageDetails.companyName')}
                    rules={[{required: true, message: t('central.carrier.messages.pleaseInputCompanyName') as string}]}
                >
                    <Input placeholder={t('central.carrier.placeholders.enterCompanyName') as string}/>
                </Form.Item>



                <Form.Item
                    name="contactName"
                    label={t('central.carrier.pageDetails.contactName')}
                    rules={[{required: true, message: t('central.carrier.messages.pleaseInputContactName') as string}]}
                >
                    <Input placeholder={t('central.carrier.placeholders.enterContactName') as string}/>
                </Form.Item>

                <Form.Item
                    name="contactPhone"
                    label={t('central.carrier.pageDetails.phone') }
                    rules={[{required: true, message: t('central.carrier.messages.pleaseInputPhoneNumber') as string}]}
                >
                    <Input placeholder={t('central.carrier.placeholders.enterPhoneNumber') as string}/>
                </Form.Item>

                <Form.Item
                    name="carrierType"
                    label={t('central.carrier.pageDetails.acceptedRouteType')}
                    rules={[{required: true, message: t('central.carrier.messages.pleaseSelectAcceptedRouteType') as string}]}
                >
                    <Select
                        placeholder={t('central.carrier.placeholders.selectAcceptedRouteType') as string}
                        options={[
                            {value: 1, label: t('central.carrier.types.trunk')},
                            {value: 2, label: t('central.carrier.types.branch')},
                            {value: 3, label: t('central.carrier.types.trunkAndBranch')},
                        ]}
                    />
                </Form.Item>

                <Form.Item
                    name="centerWarehouseIds"
                    label={t('central.carrier.pageDetails.warehouse')}
                    rules={[{required: true, message: t('central.carrier.messages.pleaseSelectWarehouse') as string}]}

                >
                    <Select
                        mode="multiple"
                        placeholder={t('central.carrier.placeholders.selectWarehouses') as string}
                        options={warehouseOptions}
                    />
                </Form.Item>

                <Form.Item
                    name="status"
                    label={t('central.carrier.pageDetails.status')}
                    initialValue={1}
                    rules={[{required: true, message: t('central.carrier.messages.pleaseSelectStatus') as string}]}
                >
                    <Select
                        placeholder={t('central.carrier.placeholders.selectStatus') as string}
                        options={[
                            {value: 1, label: t('enum.activeStatus.active')},
                            {value: 0, label: t('enum.activeStatus.suspended')},
                        ]}
                    />
                </Form.Item>

            </Form>
        </EditableDrawer>
    );
};

export default CarrierFormDrawer;